/**
 * Etiquetas de conversa — catálogo e aplicação.
 *
 * ── Por que existe um CATÁLOGO, e não texto livre ─────────────────────────
 *
 * Medido contra o motor em 01/09/2026: colar um título inédito numa conversa
 * responde 200 e **não** acrescenta a etiqueta ao catálogo da conta. Ela passa
 * a existir só naquela conversa — sem cor, e ausente de qualquer lista
 * construída a partir do catálogo.
 *
 * Ou seja: texto livre produz etiqueta de segunda classe, que funciona hoje e
 * some de todo lugar que importa depois. É o "não falha, some" em forma de
 * etiqueta.
 *
 * Por isso criar etiqueta é um ATO — `POST /v1/etiquetas`, que a põe no
 * catálogo com cor — e aplicá-la é outro. A tela pode encadear os dois num
 * gesto só; o contrato não finge que é um.
 *
 * ── A cor mora no catálogo, e não na conversa ─────────────────────────────
 *
 * Junta, trocar a cor de uma etiqueta exigiria reprojetar toda conversa que a
 * tem — e até lá a mesma etiqueta sairia de duas cores em linhas vizinhas da
 * mesma fila. A conversa carrega o TÍTULO; a cor a tela busca aqui, uma vez.
 */

import { z } from 'zod';

/** Hexadecimal de seis dígitos. É o formato que o motor guarda e devolve. */
const CorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Cor em hexadecimal de seis dígitos, como #1F93FF.');

export const EtiquetaSchema = z
  .object({
    titulo: z.string().min(1),
    /**
     * `null` quando o motor não definiu cor.
     *
     * A tela escolhe uma cor neutra nesse caso, e não uma sorteada: etiqueta
     * que muda de cor entre um carregamento e outro parece defeito.
     */
    cor: CorSchema.nullable(),
  })
  .strict();

export type Etiqueta = z.infer<typeof EtiquetaSchema>;

export const CatalogoDeEtiquetasSchema = z
  .object({
    itens: z.array(EtiquetaSchema),
    /** O catálogo vem do motor a cada leitura; sem projeção, sem defasagem. */
    stale: z.literal(false),
  })
  .strict();

export const CriarEtiquetaSchema = z
  .object({
    /*
     * Sem espaço, e em minúsculas — é como o motor as guarda e as compara.
     *
     * Aceitar "Novo Lead" criaria uma etiqueta que o filtro por `novo-lead`
     * nunca encontra, e a diferença entre as duas é invisível numa pastilha.
     */
    titulo: z
      .string()
      .trim()
      .min(1, 'A etiqueta precisa de um nome.')
      .max(40, 'Nome de etiqueta acima de 40 caracteres não cabe numa pastilha.')
      .regex(
        /^[a-z0-9à-ÿ]+(?:[-_][a-z0-9à-ÿ]+)*$/,
        'Use minúsculas, sem espaço — separe com hífen, como `novo-lead`.',
      ),
    cor: CorSchema,
  })
  .strict();

export const DefinirEtiquetasSchema = z
  .object({
    /*
     * A lista INTEIRA, e não "acrescentar esta".
     *
     * É a semântica do motor, medida: o `POST` dele substitui. Um contrato que
     * dissesse "acrescentar" e chamasse uma API que substitui apagaria as
     * outras etiquetas — e o sintoma seria etiqueta sumindo sozinha, que
     * ninguém liga à ação que a apagou.
     */
    etiquetas: z
      .array(z.string().trim().min(1))
      .max(20, 'Vinte etiquetas numa conversa já não ajudam ninguém a achar nada.'),
  })
  .strict();

export const EtiquetasDaConversaSchema = z
  .object({
    /** O que o MOTOR diz que ficou, e não o que foi pedido. */
    etiquetas: z.array(z.string()),
  })
  .strict();

/**
 * As etiquetas PRÓPRIAS de um cliente, depois de substituídas.
 *
 * ── Por que não reusa a schema da conversa ────────────────────────────────
 *
 * Porque as duas afirmam coisas diferentes, e a diferença é o assunto desta
 * rota. Lá, a resposta é o que o MOTOR diz que ficou — a gravação é dele, e
 * pedir não é o mesmo que conseguir. Aqui a gravação é nossa, na mesma
 * transação, então o que volta é o que ficou no banco.
 *
 * Duas linhas iguais que querem dizer coisas diferentes valem mais que uma
 * compartilhada que esconde qual das duas verdades está sendo dita.
 */
export const EtiquetasDoClienteSchema = z
  .object({
    etiquetas: z.array(z.string()),
  })
  .strict();

/**
 * O que sobrou de não lido depois de marcar a conversa como vista.
 *
 * Vem do MOTOR, relido depois da marcação — não é o zero que a gente pediu.
 * É a mesma regra de `EtiquetasDaConversaSchema` logo acima, e pela mesma
 * razão: a tela que pinta o número pedido em vez do número real mente
 * exatamente na hora em que a marcação falhou.
 *
 * Nem sempre é zero, e é de propósito: mensagem pode chegar entre a marcação
 * e a releitura. Um zero forçado aqui esconderia a mensagem que acabou de
 * entrar.
 */
export const ConversaLidaSchema = z
  .object({
    nao_lidas: z.number().int().nonnegative(),
  })
  .strict();

/**
 * A prioridade pedida. `null` limpa.
 *
 * Os mesmos quatro degraus de `ConversaCompostaSchema` — e não os do motor.
 * `media`, e não `medium`: o vocabulário do contrato é o nosso (§3.2), e a
 * tradução mora na fronteira com a engine.
 */
export const DefinirPrioridadeSchema = z
  .object({
    prioridade: z.enum(['baixa', 'media', 'alta', 'urgente']).nullable(),
  })
  .strict();

/**
 * O que o MOTOR diz que ficou, e não o que foi pedido.
 *
 * Mesma regra de `EtiquetasDaConversaSchema`: a tela que pinta o valor pedido
 * mente exatamente na hora em que a gravação falhou.
 */
export const PrioridadeDaConversaSchema = z
  .object({
    prioridade: z.enum(['baixa', 'media', 'alta', 'urgente']).nullable(),
  })
  .strict();

export type EtiquetasDoCliente = z.infer<typeof EtiquetasDoClienteSchema>;
export type DefinirPrioridade = z.infer<typeof DefinirPrioridadeSchema>;
export type PrioridadeDaConversa = z.infer<typeof PrioridadeDaConversaSchema>;
